import React, { useState, useCallback, useEffect, useMemo, useRef } from "react";
import { Box, Button, Icon, pickFiles, pickImageFile, Text } from "@salestrak/ui";
import { RichTextEditor, withShortcutHints } from "#app/components/editor/rich-text-editor";
import { useActivityContext } from "./activity-context";
import { useIssueViewCapabilities } from "../issue-view-capabilities";
import {
  NoteMediaAttachments,
  type NoteMediaCard,
} from "./note-media-attachments";
import { isRichTextEmpty } from "#app/lib/utils/rich-text-value";
import { extractMentionedUserIds } from "#app/lib/utils/extract-mentions";
import { useMentionAccessActions } from "#app/lib/mention-access-dialog-context";
import { useNoteMediaUpload, useResolvedMediaHtml } from "#app/lib/hooks/use-note-media";
import { usePendingMediaUploads } from "#app/lib/hooks/use-pending-media-uploads";
import { restoreMediaHtml } from "#app/lib/media-content";
import { NOTE_MEDIA_ACCEPT } from "#app/lib/media-kind";
import { canManageSharing as canManageSharingFn } from "#app/lib/utils/access-level";
import { useFeatureFlags, FeatureFlagKey } from "#app/hooks/use-feature-flags";
import type { Omittable } from "@salestrak/ui";

type NoteComposerProps = Omittable<{
  issueId: string;
  parentNoteId?: string | null;
  editMode?: boolean;
  editNoteId?: string | null;
  initialContent?: string;
  startExpanded?: boolean;
  onCancel?: () => void;
  onSubmit?: () => void;
}>;

/**
 * Expandable rich text composer for notes/comments.
 * Supports three modes: new parent note, reply, and edit.
 * Starts collapsed (placeholder), expands on click to show compact TipTap editor.
 */
export function NoteComposer({
  issueId,
  parentNoteId = null,
  editMode = false,
  editNoteId = null,
  initialContent = "",
  startExpanded = false,
  onCancel,
  onSubmit,
}: NoteComposerProps) {
  const { createNote, updateNote } = useActivityContext("NoteComposer");
  const { canComment, level } = useIssueViewCapabilities();
  const canManageItemSharing = canManageSharingFn(level);
  const { hasFlag } = useFeatureFlags();
  const photosEnabled = hasFlag(FeatureFlagKey.PHOTOS);

  // `mountExpanded` keeps the editor mounted while the height animates down;
  // swapping to the short collapsed row in the same commit would leave nothing
  // for the `auto`-height transition to resolve against.
  const [expanded, setExpanded] = useState(editMode || startExpanded);
  const [mountExpanded, setMountExpanded] = useState(editMode || startExpanded);
  const [content, setContent] = useState(initialContent);
  const [submitting, setSubmitting] = useState(false);
  // Footer photo attachments (cards under the editor). Inline (slash) photo
  // ids are tracked separately — they live in the HTML, not the card row,
  // and reach createNote as inlineMediaIds for permission linking.
  const [attachments, setAttachments] = useState<NoteMediaCard[]>([]);
  // Footer uploads: optimistic, cancellable progress tiles that commit into
  // `attachments` on success. The pending-upload state machine (temp ids,
  // abort controllers, unmount cleanup) is shared with the photos custom field.
  const {
    pending,
    startUploads: startFooterUploads,
    cancel: cancelFooterUpload,
    reset: resetFooterUploads,
  } = usePendingMediaUploads({
    onUploaded: ({ photoId, objectUrl, contentType, fileName }) =>
      setAttachments((prev) => [
        ...prev,
        { photoId, uri: objectUrl, contentType, fileName },
      ]),
  });
  // Inline (slash-command) uploads in flight — counted only, since their
  // progress shows in the editor image node, not a footer tile.
  const [inlineUploadingCount, setInlineUploadingCount] = useState(0);
  // Send is disabled while ANY upload is in flight (footer or inline) so a note
  // can't post before its photos finish and silently drop them.
  const uploading = pending.length > 0 || inlineUploadingCount > 0;
  const inlineMediaIds = useRef<Set<string>>(new Set());
  // Abort controllers for in-flight inline (slash) uploads — aborted on unmount
  // (the footer path's equivalent lives in usePendingMediaUploads).
  const inlineControllers = useRef<Set<AbortController>>(new Set());
  const { uploadNotePhoto } = useNoteMediaUpload();
  const isEmpty = useMemo(() => isRichTextEmpty(content), [content]);
  const editorKeyRef = useRef(0);
  const collapseTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  // Edit mode: inline photo srcs in stored HTML are canonical content-route
  // paths — swap to authenticated object URLs so the editor displays them.
  // Usually instant (the note display already resolved + cached them). We mount
  // the editor only once `resolving` clears (below) so it starts from the
  // resolved HTML — remounting it afterwards would discard in-progress edits.
  const { html: resolvedInitial, resolving: resolvingInitial } =
    useResolvedMediaHtml(initialContent);
  const editorInitialContent = editMode ? resolvedInitial : "";
  const editorKey = editorKeyRef.current;

  useEffect(() => {
    const controllers = inlineControllers.current;
    return () => {
      if (collapseTimer.current) clearTimeout(collapseTimer.current);
      for (const controller of controllers) controller.abort();
    };
  }, []);

  // Mention-access guard: after the note posts, the globally-mounted
  // MentionAccessDialogMount checks the @-mentioned ids and pops the
  // "they don't have access" prompt when any of them can't see the
  // issue. The note itself never blocks on the check.
  const { promptIfMissing } = useMentionAccessActions();

  const placeholder = editMode
    ? "Edit your note"
    : parentNoteId
      ? "Write a reply"
      : "Write a comment";

  const submitLabel = editMode ? "Save" : parentNoteId ? "Reply" : "Send";

  const handleExpand = useCallback(() => {
    // Re-expanding mid-collapse: cancel the pending content swap to keep the editor.
    if (collapseTimer.current) {
      clearTimeout(collapseTimer.current);
      collapseTimer.current = null;
    }
    setExpanded(true);
    setMountExpanded(true);
  }, []);

  // Animate closed, then swap content + reset the editor AFTER the shrink.
  const collapse = useCallback(() => {
    setExpanded(false);
    if (collapseTimer.current) clearTimeout(collapseTimer.current);
    collapseTimer.current = setTimeout(() => {
      collapseTimer.current = null;
      setContent("");
      setAttachments([]);
      resetFooterUploads();
      inlineMediaIds.current.clear();
      editorKeyRef.current += 1;
      setMountExpanded(false);
    }, COLLAPSE_DURATION_MS);
  }, [resetFooterUploads]);

  const handleUpdate = useCallback((html: string) => {
    setContent(html);
  }, []);

  // Footer path: prompt for files (multi-select); the shared hook uploads each
  // on its own (optimistic tile with live preview + progress) and commits it to
  // `attachments` on success. Failed uploads toast (in the hook) and drop.
  // Notes accept images, video, PDF, and common office docs; the photos custom
  // field stays image-only (its own pickImageFiles path).
  const handleAttachPhoto = useCallback(async () => {
    await startFooterUploads(await pickFiles(NOTE_MEDIA_ACCEPT, true));
  }, [startFooterUploads]);

  const handleRemoveAttachment = useCallback((photoId: string) => {
    setAttachments((prev) => prev.filter((p) => p.photoId !== photoId));
  }, []);

  // Drag-reorder: the card row's order IS the submitted mediaIds order,
  // which the server persists as each photo's sort_order.
  const handleReorderAttachments = useCallback((mediaIds: string[]) => {
    setAttachments((prev) => {
      const byId = new Map(prev.map((p) => [p.photoId, p]));
      const next = mediaIds
        .map((id) => byId.get(id))
        .filter((p): p is NoteMediaCard => !!p);
      return next.length === prev.length ? next : prev;
    });
  }, []);

  // Slash path: same prompt + media upload; the editor inserts the returned
  // object URL as an image node, restored to the canonical content-route src
  // at submit. Inline uploads don't render a footer tile, so they aren't in the
  // pending-uploads hook — but they still get an abort controller so an
  // in-flight slash upload is cancelled if the composer unmounts.
  const handlePickInlinePhoto = useCallback(async () => {
    const file = await pickImageFile();
    if (!file) return null;
    const controller = new AbortController();
    inlineControllers.current.add(controller);
    setInlineUploadingCount((c) => c + 1);
    try {
      const uploaded = await uploadNotePhoto(file, { signal: controller.signal });
      // Inline picks are always images, so the preview object URL is present;
      // guard anyway to satisfy the editor's non-null src contract.
      if (!uploaded?.objectUrl) return null;
      inlineMediaIds.current.add(uploaded.photoId);
      return { objectUrl: uploaded.objectUrl };
    } finally {
      inlineControllers.current.delete(controller);
      setInlineUploadingCount((c) => c - 1);
    }
  }, [uploadNotePhoto]);

  const handleSubmit = useCallback(async () => {
    const hasAttachments = attachments.length > 0;
    if ((isEmpty && !hasAttachments) || submitting) return;

    setSubmitting(true);
    // Inline photos render from session object URLs — persist the canonical
    // /media/:id/content srcs instead (environment-independent, resolvable
    // by every viewer).
    const submitted = restoreMediaHtml(content);
    const mentionedIds = extractMentionedUserIds(submitted);
    try {
      if (editMode && editNoteId) {
        await updateNote(editNoteId, submitted);
        onCancel?.();
      } else {
        const footerIds = attachments.map((p) => p.photoId);
        const inlineIds = [...inlineMediaIds.current];
        await createNote({
          note: submitted,
          issueId,
          parentNoteId: parentNoteId || null,
          mediaIds: footerIds.length ? footerIds : null,
          inlineMediaIds: inlineIds.length ? inlineIds : null,
        });
        if (parentNoteId) {
          // Reply composers stay open — reset for the next reply.
          setContent("");
          setAttachments([]);
          resetFooterUploads();
          inlineMediaIds.current.clear();
          editorKeyRef.current += 1;
        } else {
          // Clear content synchronously before `finally` re-enables the button so
          // the empty-guard blocks a rapid second Send (would post a duplicate).
          setContent("");
          collapse();
        }
      }
      onSubmit?.();
      // Background check — fires AFTER the note is posted so a slow /
      // failing lookup never strands the comment.
      if (mentionedIds.length) {
        promptIfMissing({
          issueId,
          userIds: mentionedIds,
          viewerCanManageSharing: canManageItemSharing,
        });
      }
    } catch (error) {
      console.error("Failed to send note:", error);
    } finally {
      setSubmitting(false);
    }
  }, [
    content,
    attachments,
    isEmpty,
    submitting,
    editMode,
    editNoteId,
    issueId,
    parentNoteId,
    createNote,
    updateNote,
    collapse,
    resetFooterUploads,
    onCancel,
    onSubmit,
    promptIfMissing,
    canManageItemSharing,
  ]);

  const handleCancel = useCallback(() => {
    if (editMode) {
      onCancel?.();
    } else {
      collapse();
    }
  }, [editMode, onCancel, collapse]);

  // View-only access blocks every composer mode (new note, reply, edit) —
  // the backend rejects regardless, this just keeps the UI honest.
  if (!canComment) return null;

  // Edit mode renders frameless: the host note card owns the border/background.
  if (editMode) {
    return (
      <Box>
        <Box maxHeight={400} overflow="auto">
          {/* Mount only once inline-photo resolution settles, so the editor
              starts from the resolved HTML. Remounting it afterwards (the old
              key-flip approach) discarded edits typed during resolution. */}
          {!resolvingInitial && (
            <RichTextEditor
              key={editorKey}
              content={editorInitialContent}
              onUpdate={handleUpdate}
              placeholder={placeholder}
              shortcutHints
              mentions
              compact
              autoFocus
            />
          )}
        </Box>
        <Box
          direction="row"
          align="center"
          justify="end"
          gap="sm"
          paddingTop="sm"
        >
          <Button variant="ghost" size="sm" onClick={handleCancel}>
            Cancel
          </Button>
          <Button
            size="sm"
            onClick={handleSubmit}
            disabled={isEmpty || submitting || uploading}
            loading={submitting}
          >
            {submitLabel}
          </Button>
        </Box>
      </Box>
    );
  }

  // One persistent root for both states so the collapse/expand height change is
  // a CSS transition on the same element. The root is never the button — the
  // collapsed row owns the click, so the root element survives the swap and the
  // transition runs.
  return (
    <Box
      border
      rounded="lg"
      // Fill only — `surface` would add a card shadow and float the composer
      // off the panel; it is a flat bordered field.
      background="card"
      overflow="hidden"
      height={expanded ? "auto" : COLLAPSED_HEIGHT}
      className={COLLAPSE_TRANSITION}
    >
      {mountExpanded ? (
        <>
          <Box maxHeight={400} overflow="auto">
            <RichTextEditor
              key={editorKey}
              content={editorInitialContent}
              onUpdate={handleUpdate}
              placeholder={placeholder}
              shortcutHints
              mentions
              compact
              autoFocus
              contentPaddingX="md"
              contentPaddingY="sm"
              onPickPhoto={photosEnabled ? handlePickInlinePhoto : undefined}
            />
          </Box>
          {(attachments.length > 0 || pending.length > 0) && (
            <Box paddingX="md" paddingBottom="sm">
              <NoteMediaAttachments
                photos={attachments}
                pending={pending}
                onRemove={handleRemoveAttachment}
                onCancelPending={cancelFooterUpload}
                onReorder={handleReorderAttachments}
              />
            </Box>
          )}
          <Box
            direction="row"
            align="center"
            justify="between"
            padding="sm"
          >
            <Box direction="row" gap="xs">
              {photosEnabled && (
                <Button
                  variant="ghost"
                  size="icon-sm"
                  onClick={handleAttachPhoto}
                  aria-label="Attach photo"
                >
                  <Icon name="image" />
                </Button>
              )}
            </Box>
            <Box direction="row" gap="sm" align="center">
              {(editMode || expanded) && (
                <Button variant="ghost" size="sm" onClick={handleCancel}>
                  Cancel
                </Button>
              )}
              <Button
                size="sm"
                onClick={handleSubmit}
                disabled={
                  (isEmpty && attachments.length === 0) ||
                  submitting ||
                  uploading
                }
                loading={submitting}
              >
                {submitLabel}
              </Button>
            </Box>
          </Box>
        </>
      ) : (
        <Box
          as="button"
          direction="row"
          align="center"
          justify="between"
          paddingX="md"
          paddingY="sm"
          fill="width"
          height={COLLAPSED_HEIGHT}
          hoverBackground
          onClick={handleExpand}
        >
          <Text size="sm" variant="muted">
            {withShortcutHints(placeholder)}
          </Text>
        </Box>
      )}
    </Box>
  );
}

// Explicit height gives the transition a concrete start/end point.
const COLLAPSED_HEIGHT = 38;

// The CSS transition (duration-200 on the root) and the JS swap timer read the
// same number so they can't drift. `interpolate-size` is what lets the
// expanded `height: auto` animate at all (Chrome 129+; snaps elsewhere).
const COLLAPSE_DURATION_MS = 200;
const COLLAPSE_TRANSITION =
  "transition-[height] duration-200 ease-in-out [interpolate-size:allow-keywords]";
